import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, HelpCircle } from 'lucide-react';

interface SingleOrgFormProps {
  onSubmit: (data: any) => Promise<void>;
  onBack: () => void;
}

export function SingleOrgForm({ onSubmit, onBack }: SingleOrgFormProps) {
  const [tinName, setTinName] = useState('');
  const [tin, setTin] = useState('');
  const [practiceAlias, setPracticeAlias] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onSubmit({
        tinName,
        tin,
        practiceAlias,
        // clientAlias will be derived from tinName on the backend
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Register Your Organization</CardTitle>
            <CardDescription>
              Please provide the details for your organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="tinName">TIN Name (Official Organization Name)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>This will be used as the primary name for your organization in the registry.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  id="tinName"
                  value={tinName}
                  onChange={(e) => setTinName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="tin">TIN # (Tax ID Number)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Your organization's 9-digit Taxpayer Identification Number.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  id="tin"
                  value={tin}
                  onChange={(e) => setTin(e.target.value)}
                  required
                  placeholder="9-digit TIN"
                  maxLength={9}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="practiceAlias">Practice Alias (Optional)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>A nickname to help you easily identify this practice.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  id="practiceAlias"
                  value={practiceAlias}
                  onChange={(e) => setPracticeAlias(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <Button type="button" variant="ghost" onClick={onBack}>
                  &larr; Back
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? 'Registering...' : 'Complete Registration'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
